import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Text } from '@change/ds-core-ui';
import { TextField } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { EndorsementFormData } from '../../shared/types';

import { PhotoUploadSection } from './components/PhotoUploadSection';

type EndorsementFormFieldsProps = {
	formData: EndorsementFormData;
	statementCharCount: number;
	statementMaxLength: number;
	onFieldChange: (field: keyof EndorsementFormData, value: string) => void;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
};

export const EndorsementFormFields: React.FC<EndorsementFormFieldsProps> = ({
	formData,
	statementCharCount,
	statementMaxLength,
	onFieldChange,
	uppy,
	image,
}) => {
	const { translate } = useI18n();

	return (
		<>
			{/* Name Field */}
			<TextField
				name="endorserName"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_helper')}
				value={formData.name || ''}
				onChange={(value) => onFieldChange('name', value)}
				data-testid="endorser-name-field"
				data-qa="endorser-name-field"
				size="large"
				boldLabel
			/>

			{/* Title/Description Field */}
			<TextField
				name="endorserTitle"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.title_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.title_helper')}
				value={formData.title || ''}
				onChange={(value) => onFieldChange('title', value)}
				data-testid="endorser-title-field"
				data-qa="endorser-title-field"
				size="large"
				boldLabel
			/>

			{/* Statement Field (Optional) */}
			<div className="flex flex-col gap-1">
				<TextField
					name="endorserStatement"
					inputElementType="textarea"
					label={`${translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_label')} ${translate('corgi.petition.starter_dashboard_v2.endorsements.add.optional_label')}`}
					supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_helper')}
					value={formData.statement || ''}
					onChange={(value) => onFieldChange('statement', value)}
					inputOptions={{
						maxLength: statementMaxLength,
						className: 'min-h-28',
					}}
					data-testid="endorser-statement-field"
					data-qa="endorser-statement-field"
					size="large"
					boldLabel
				/>
				<div className="flex items-center justify-end gap-6">
					<Text variant="body" size="x-small" className="text-typography-weak text-right">
						{statementCharCount}/{statementMaxLength}
					</Text>
				</div>
			</div>

			{/* Photo Upload Section */}
			<PhotoUploadSection image={image} uppy={uppy} />

			{/* URL Field */}
			<TextField
				name="endorserUrl"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.url_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.url_helper')}
				value={formData.url || ''}
				onChange={(value) => onFieldChange('url', value)}
				data-testid="endorser-url-field"
				data-qa="endorser-url-field"
				size="large"
				boldLabel
			/>
		</>
	);
};
